import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";
import { getItemDescription, handleInspect } from "../../../api/src/base/util/inspect";
import { itemNameRegex } from "../../../api/src/base/util/regex";
import { gameCanvasStyles } from "./style";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = gameCanvasStyles;

    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];
    private inventory: string[] = [];

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        if (this.roomTitle !== state.roomTitle) {
            this.selectedInventoryItem?.classList.remove("selected-inventory-item");
            this.selectedInventoryItem = null;
            const inspectOption: HTMLSpanElement | null = this.shadowRoot?.querySelector(".inspect-option") as HTMLSpanElement | null;
            if (inspectOption) {
                inspectOption.remove();
            }
        }
    
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        this.inventory = state.inventory;
    
        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();
    
        this.requestUpdate();
    }
    

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async ResetGame(): Promise<void> {
        const state: any = await performAction("reset-game");
        this.updateState(state);
    }

    private selectedInventoryItem: HTMLElement | null = null;
    private handleClickInventoryItem(event: MouseEvent): void {
        const target: HTMLElement = event.target as HTMLElement;
        const inventoryItem: HTMLElement = target.closest(".inventory-item") as HTMLElement;
    
        if (inventoryItem === this.selectedInventoryItem) {
            inventoryItem.classList.remove("selected-inventory-item");
            this.selectedInventoryItem = null;
            const inventoryContainer: HTMLElement | null = inventoryItem.parentElement;
            if (inventoryContainer) {
                const inspectOption: HTMLSpanElement | null = inventoryContainer.querySelector(".inspect-option");
                if (inspectOption) {
                    inspectOption.remove();
                }
            }
        } else {
            if (this.selectedInventoryItem) {
                this.selectedInventoryItem.classList.remove("selected-inventory-item");
                const prevInspectOption: HTMLSpanElement | null = this.selectedInventoryItem.parentElement?.querySelector(".inspect-option") ?? null;
                if (prevInspectOption) {
                    prevInspectOption.remove();
                }
            }
            inventoryItem.classList.add("selected-inventory-item");
            this.selectedInventoryItem = inventoryItem;
            const inventoryContainer: HTMLElement | null = inventoryItem.parentElement;
            if (inventoryContainer) {
                const itemName: string = inventoryItem.textContent?.trim() || "";
                const itemDescription: string = getItemDescription(itemName, this.roomTitle || "", this.contentText?.join(" ") || "");
                const inspectOption: HTMLSpanElement = document.createElement("span");
                inspectOption.classList.add("inspect-option");
                inspectOption.textContent = "--- Inspect";
                inspectOption.addEventListener("click", () => {
                    handleInspect(itemName, itemDescription);
                    inventoryItem.classList.remove("selected-inventory-item");
                    inspectOption.remove();
                });
                inventoryContainer.appendChild(inspectOption);
            }
        }
    }
    
    

    private itemNameRegex(text: string): string {
        return itemNameRegex(text);
    }


    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    protected render(): TemplateResult {
        return html`
            <div class="game" style="background-image: ${this.roomImages ? `url(/assets/img/rooms/${this.roomImages[0]}.png)` : "none"};">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
        `;
    }


    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`
                <div class="title">
                    ${this.roomTitle}
                    <div class="buttons">
                        <button class="button reset" @click=${this.ResetGame}>Reset game</button>
                    </div>
                </div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 1) {
            return html`
                <div class="header">
                <img src="/assets/img/rooms/${this.roomImages[1]}.png">
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        const isStartupRoom: boolean = this.roomTitle === "Start Game";
        const isInfoRoom: boolean = this.roomTitle === "Information Room";
        const isWizardRoom: boolean = this.roomTitle === "Wizard Room";
        const contentClass: string = isStartupRoom ? "content start": (isInfoRoom ? "info-room-content" : (isWizardRoom ? "wizard-room-content" : "content"));
    
        return html`
            <div class="${contentClass}">
            
                ${this.contentText?.map((text) => html`<p>${text}</p>`)}
            </div>
        `;
    }
    
    private renderFooter(): TemplateResult {
        const isStartupRoom: boolean = this.roomTitle === "Start Game";
        const isInfoRoom: boolean = this.roomTitle === "Information Room";
        const isWizardRoom: boolean = this.roomTitle === "Wizard Room";
        const isFinishRoom: boolean = this.roomTitle === "Finish Room";
        const startButtonClass: string = isStartupRoom ? "startup-room-start-button" : "";
        const footerClass: string = isInfoRoom ? "info-room-footer" : (isStartupRoom ? "startup-room-footer" : (isWizardRoom ? "wizard-room-footer" : ""));
        const showInventory: boolean = !isInfoRoom && !isFinishRoom && !isStartupRoom;
    
        return html`
            <div class="footer ${footerClass}">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""} ${button.alias === "start-game" ? startButtonClass : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                            >${button.label}</a>`
                        )}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                (button) => html`<a
                                    class="button ${this.selectedGameObjectButtons.has(button) ? "active" : ""}"
                                    @click=${(): void => void this.handleClickObject(button)}
                                >${button.name}</a>`
                            )
                            : nothing}
                    </div>
                </div>
            </div>
            ${showInventory
                ? html`
                    <div class="inventory">
                        <p class="inventory-name">Inventory:</p>
                        <ul>
                            ${this.inventory.length > 0 
                                ? this.inventory.map((item, index) => html`
                                    <li>
                                        <span class="inventory-item ${this.selectedInventoryItem?.textContent === item ? "selected" : ""}" @click="${(event: MouseEvent): void => this.handleClickInventoryItem(event)}">
                                            ${index + 1}. ${this.itemNameRegex(item)}
                                        </span>
                                    </li>`
                                )
                                : html`<li>Empty</li>`
                            }
                        </ul>
                    </div>`
                : nothing}
        `;
    }
}